import React from "react";
import { useQuery } from "@apollo/client";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Tooltip,
  Typography,
  Stack,
  Avatar,
  useTheme,
} from "@mui/material";
import {
  ExpandLess as ExpandLessIcon,
  ExpandMore as ExpandMoreIcon,
  Info as InfoIcon,
  MonitorHeartOutlined as MonitorHeartOutlinedIcon,
  Tag as TagIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Pending as PendingIcon,
  EditOutlined as EditOutlinedIcon,
} from "@mui/icons-material";
import { EvidenceStatus, EmptyFields } from "../../../../../utils/enums";
import BlinkingStatusDot from "../../../../../components/BlinkingStatusDot/BlinkingStatusDot";
import { CheckCategoryIcons } from "./CheckCategoryIcons";
import { CheckStatusChip } from "../../../../../components/CheckStatusChip/CheckStatusChip";
import { formatReason } from "../../../../../utils/reasonFormatting";
import ControlOwnerAvatar from "../../../../../components/ControlOwnerAvatar/ControlOwnerAvatar";
import AIEvidenceChip from "../../../../../components/AIEvidenceChip/AIEvidenceChip";
import { GET_AI_EVIDENCE_SUMMARY } from "../../../../../apis/integrated-evidence/aiEvidenceApis";
import useAppStore from "../../../../../stores";
import {
  FEATURE_IDS,
  useFeatureEnabled,
} from "../../../../../components/AccessControl";

const ChecksSection = ({ control, refetchData }) => {
  const selectedFrameworkId = useAppStore((state) => state.selectedFrameworkId);
  const aiEvidenceEnabled = useFeatureEnabled(
    FEATURE_IDS.REGO_AI_EVIDENCE_COLLECTION
  );
  const { data: aiSummaryData } = useQuery(GET_AI_EVIDENCE_SUMMARY, {
    variables: { frameworkId: selectedFrameworkId },
    skip: !aiEvidenceEnabled || !selectedFrameworkId,
    fetchPolicy: "cache-first",
  });

  const aiEvidenceByCheck = React.useMemo(() => {
    const map = new Map();
    (aiSummaryData?.getAIEvidenceSummary?.byCheck || []).forEach((entry) => {
      map.set(entry.checkId, entry);
    });
    return map;
  }, [aiSummaryData]);

  // Sort checks by name in ascending order
  const sortedChecks = React.useMemo(() => {
    if (!control?.checks) return [];
    return [...control.checks].sort((a, b) => {
      const nameA = (a?.activeCheckName || a?.checkName || "").toLowerCase();
      const nameB = (b?.activeCheckName || b?.checkName || "").toLowerCase();
      return nameA.localeCompare(nameB);
    });
  }, [control?.checks]);

  return (
    <Card
      sx={{
        borderRadius: 2,
        mb: 3,
      }}
      variant="outlined"
    >
      <Box
        sx={{
          borderBottom: "1px solid #ddd",
          padding: "16px",
          display: "flex",
          alignItems: "center",
          gap: "15px",
        }}
      >
        <MonitorHeartOutlinedIcon />
        <Typography variant="body1" fontWeight="bold">
          Checks
        </Typography>
      </Box>
      <CardContent sx={{ padding: "unset", paddingBottom: "unset !important" }}>
        <List
          sx={{
            padding: 0,
            margin: 0,
            borderRadius: 1,
            overflow: "hidden",
            border: "1px solid #e0e0e0",
            boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          }}
        >
          {sortedChecks.map((check, index) => {
            return (
              <React.Fragment key={`check-${index}`}>
                {index > 0 && (
                  <Divider sx={{ height: "1px", backgroundColor: "#e0e0e0" }} />
                )}
                <Tooltip
                  title={"Click to view check details"}
                  placement="top"
                  arrow
                >
                  <ListItem
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "flex-start",
                      padding: 2.5,
                      backgroundColor: index % 2 === 0 ? "#f5f7fa" : "#ffffff",
                      cursor: "pointer",
                      transition: "all 0.2s ease-in-out",
                      "&:hover": {
                        backgroundColor:
                          index % 2 === 0 ? "#f0f9ff" : "#f0f9ff",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        borderRadius: "4px",
                        transform: "translateY(-1px)",
                      },
                    }}
                    onClick={() => {
                      const checkType = check?.checkType || check?.activeCheckType;
                      if (checkType) {
                        window.open(`/checks/${checkType}`, "_blank");
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        mb: 1,
                      }}
                    >
                      <Chip
                        label={`#${index + 1}`}
                        size="small"
                        sx={{
                          height: 28,
                          fontSize: "0.8rem",
                          fontWeight: 700,
                          backgroundColor: "#1976d2",
                          color: "white",
                          "& .MuiChip-label": {
                            padding: "0 10px",
                          },
                        }}
                      />
                      <Chip
                        label={
                          check?.activeCheckName
                            ?.replace(/_/g, " ")
                            ?.toUpperCase() ?? EmptyFields.UNKNOWN
                        }
                        size="small"
                        color="default"
                        sx={{
                          fontWeight: "bold",
                          textTransform: "uppercase",
                          borderRadius: "8px",
                          backgroundColor: "#f3f4f6",
                          color: "#333",
                        }}
                      />
                      {CheckCategoryIcons(check?.activeCheckCategory)}
                      {aiEvidenceEnabled && (() => {
                        const counts = aiEvidenceByCheck.get(
                          check?.activeCheckId
                        );
                        if (!counts?.pendingCount && !counts?.approvedCount) {
                          return null;
                        }
                        return (
                          <AIEvidenceChip
                            pendingCount={counts?.pendingCount || 0}
                            approvedCount={counts?.approvedCount || 0}
                            myPendingCount={counts?.myPendingCount || 0}
                          />
                        );
                      })()}
                    </Box>

                    {/* Check Description */}
                    <Box
                      sx={{
                        width: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <ListItemText
                        primary={
                          check?.activeCheckDescription ??
                          check?.checkDescription
                        }
                        primaryTypographyProps={{
                          variant: "body2",
                          fontWeight: 400,
                          color: "text.primary",
                        }}
                        sx={{ flex: 1, mr: 2 }}
                      />
                      {/* Status Chip with E badge overlay */}
                      <Box
                        sx={{ position: "relative", display: "inline-block" }}
                      >
                        <Tooltip
                          title={formatReason(check?.checkStatusReason)}
                          arrow
                        >
                          <span>
                            <CheckStatusChip status={check?.checkStatus} />
                          </span>
                        </Tooltip>
                      </Box>
                    </Box>
                  </ListItem>
                </Tooltip>
              </React.Fragment>
            );
          })}
        </List>
      </CardContent>
    </Card>
  );
};
const ControlDetailsCard = ({ control, onEditOwner }) => {
  const theme = useTheme();
  const owner = control?.controlOwnerData;
  const hasOwner = !!(owner?.name || owner?.email);

  return (
    <Card sx={{ mb: 2, borderRadius: 2 }} variant="outlined">
      <Box
        sx={{
          borderBottom: "1px solid #ddd",
          padding: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography variant="body1" fontWeight="bold">
          {`${control?.control_name ?? control?.controlsName} : ${control?.control_title ?? control?.controlsTitle
            }`}
        </Typography>
      </Box>
      <CardContent style={{ backgroundColor: "#f9f9f9" }}>
        <Typography mt={1}>
          {control?.control_description ?? control?.controlsDescription}
        </Typography>
        <Box mt={2} display="flex" alignItems="center" gap="25px" flexWrap="wrap">
          <Typography variant="body2" color="textSecondary">
            Domain:
            <b> {control?.control_domain ?? control?.controlsDomain}</b>
          </Typography>

          {/* Owner */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Owner:
            </Typography>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.75,
                px: 1,
                py: 0.375,
              }}
            >
              <ControlOwnerAvatar
                owner={owner}
                size={22}
                fontSize="0.63rem"
                fontWeight={700}
              />
              <Typography variant="body2" sx={{ lineHeight: 1 }}>
                <b>{owner?.name ?? owner?.email ?? "Unassigned"}</b>
              </Typography>
            </Box>

            {onEditOwner && (
              <Tooltip title="Change owner" arrow>
                <IconButton
                  size="small"
                  onClick={onEditOwner}
                  sx={{
                    p: 0.25,
                    color: theme.customColors.icons.dark,
                    "&:hover": { backgroundColor: "#E8EDF8" },
                  }}
                >
                  <EditOutlinedIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};
const EvidenceListItem = ({
  fileDetail,
  index,
  currentPage,
  ITEMS_PER_PAGE,
  expandedEvidenceId,
  toggleExpand,
  evidenceChecksLoading,
  handleDownloadDocument,
  getFileValidationStatus,
  renderValidationStatusChip,
  renderStatusDot,
  renderCheckNumbers,
  renderCheckDetails,
}) => {
  const validationStatus = getFileValidationStatus(fileDetail);
  const isExpanded = expandedEvidenceId === fileDetail?.fileId;

  return (
    <React.Fragment>
      <ListItem
        sx={{
          display: "flex",
          alignItems: "center",
          borderBottom: "1px solid #ddd",
          backgroundColor: index % 2 === 0 ? "#f9f9f9" : "#ffffff",
          p: 2,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: "#555",
            marginRight: 2,
            width: "20px",
          }}
        >
          {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}.
        </Typography>

        <Box
          sx={{ flex: 1, display: "flex", flexDirection: "column", gap: 0.5 }}
        >
          <Typography
            variant="body1"
            onClick={() => {
              handleDownloadDocument(fileDetail?.path, fileDetail?.maskName);
            }}
            sx={{
              fontWeight: 500,
              color: "#1976d2",
              cursor: "pointer",
            }}
          >
            {fileDetail?.maskName}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography variant="caption" color="textSecondary">
              File Status: {validationStatus.status}
              {isExpanded && (
                <>
                  {" | Associated Checks: "}
                  {fileDetail?.associatedChecks?.length || 0}
                </>
              )}
              {isExpanded &&
                validationStatus.showChecks &&
                fileDetail?.associatedChecks?.length > 0 && (
                  <span style={{ marginLeft: "8px" }}>
                    IDs: [
                    {fileDetail.associatedChecks
                      .map((c) => c.checkId)
                      .join(", ")}
                    ]
                  </span>
                )}
            </Typography>
            {isExpanded &&
              renderCheckNumbers(
                fileDetail?.associatedChecks,
                validationStatus
              )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {renderValidationStatusChip(validationStatus)}
          {renderStatusDot(validationStatus)}

          {validationStatus.status === EvidenceStatus.VALID && (
            <IconButton
              size="small"
              onClick={() => toggleExpand(fileDetail?.fileId)}
              sx={{
                color: "primary.main",
                backgroundColor: "#f0f7ff",
                "&:hover": {
                  backgroundColor: "#e1f1ff",
                },
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          )}
        </Box>
      </ListItem>

      {validationStatus.status !== EvidenceStatus?.INVALID &&
        (isExpanded && evidenceChecksLoading[fileDetail.fileId] ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: 80,
            }}
          >
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            {renderCheckDetails(fileDetail?.associatedChecks, validationStatus)}
          </Collapse>
        ))}
    </React.Fragment>
  );
};
const EvidenceSection = ({
  totalItems,
  paginatedData,
  currentPage,
  totalPages,
  handlePageChange,
  expandedEvidenceId,
  toggleExpand,
  evidenceChecksLoading,
  handleDownloadDocument,
  getFileValidationStatus,
  renderValidationStatusChip,
  renderStatusDot,
  renderCheckNumbers,
  renderCheckDetails,
  setIsUploadDialogOpen,
  ITEMS_PER_PAGE,
}) => {
  return (
    <Card
      sx={{
        borderRadius: 2,
        mb: 3,
      }}
      variant="outlined"
    >
      <Box
        sx={{
          borderBottom: "1px solid #ddd",
          padding: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "15px",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <TagIcon />
          <Typography variant="body1" fontWeight="bold">
            Evidence
          </Typography>
          <Tooltip title="Evidence files are automatically checked against compliance requirements">
            <InfoIcon fontSize="small" sx={{ color: "#666", cursor: "help" }} />
          </Tooltip>
        </Box>
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => setIsUploadDialogOpen(true)}
        >
          Upload Evidence
        </Button>
      </Box>
      <CardContent sx={{ padding: "unset", paddingBottom: "unset !important" }}>
        {totalItems === 0 ? (
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ textAlign: "center", padding: "16px" }}
          >
            No Evidence Uploaded
          </Typography>
        ) : (
          <>
            <List sx={{ padding: 0, margin: 0 }}>
              {paginatedData?.map((fileDetail, index) => (
                <EvidenceListItem
                  key={fileDetail?.fileId}
                  fileDetail={fileDetail}
                  index={index}
                  currentPage={currentPage}
                  ITEMS_PER_PAGE={ITEMS_PER_PAGE}
                  expandedEvidenceId={expandedEvidenceId}
                  toggleExpand={toggleExpand}
                  evidenceChecksLoading={evidenceChecksLoading}
                  handleDownloadDocument={handleDownloadDocument}
                  getFileValidationStatus={getFileValidationStatus}
                  renderValidationStatusChip={renderValidationStatusChip}
                  renderStatusDot={BlinkingStatusDot}
                  renderCheckNumbers={renderCheckNumbers}
                  renderCheckDetails={renderCheckDetails}
                />
              ))}
            </List>

            {/* Pagination Component */}
            {totalPages > 1 && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                paddingTop={2}
                paddingBottom={2}
              >
                <Pagination
                  count={totalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export const renderValidationStatusChip = (validationStatus) => {
  switch (validationStatus.status) {
    case EvidenceStatus?.VALID:
      return (
        <Chip
          icon={<CheckCircleIcon fontSize="small" />}
          label={validationStatus.message}
          sx={{
            backgroundColor: "#E8FBF3",
            color: "#057F28",
            borderRadius: 1,
            fontWeight: 600,
            fontSize: "0.75rem",
            border: "none",
            "& .MuiChip-icon": {
              color: "#057F28",
            },
          }}
        />
      );
    case EvidenceStatus?.PROCESSING:
      return (
        <Chip
          icon={<PendingIcon fontSize="small" />}
          label={validationStatus.message}
          sx={{
            backgroundColor: "#FFF4E5",
            color: "#FF8800",
            borderRadius: 1,
            fontWeight: 600,
            fontSize: "0.75rem",
            border: "none",
            "& .MuiChip-icon": {
              color: "#FF8800",
            },
          }}
        />
      );
    case EvidenceStatus?.INVALID:
    default:
      return (
        <Chip
          icon={<CancelIcon fontSize="small" />}
          label={validationStatus.message}
          sx={{
            backgroundColor: "#FEEBEB",
            color: "#C71414",
            borderRadius: 1,
            fontWeight: 600,
            fontSize: "0.75rem",
            border: "none",
            "& .MuiChip-icon": {
              color: "#C71414",
            },
          }}
        />
      );
  }
};

export const renderCheckNumbers = (
  associatedChecks,
  validationStatus,
  checkNumberMap
) => {
  if (
    validationStatus.status !== EvidenceStatus?.VALID ||
    !validationStatus.showChecks
  ) {
    return null;
  }

  if (!associatedChecks || associatedChecks.length === 0) {
    return (
      <Stack direction="row" sx={{ marginLeft: 1, mt: 0.5 }}>
        <Chip
          label="No checks"
          size="small"
          sx={{
            height: 18,
            fontSize: "0.65rem",
            fontWeight: 500,
            backgroundColor: "#f8fafc",
            color: "#64748b",
            border: "1px solid #e2e8f0",
            "& .MuiChip-label": {
              padding: "0 4px",
            },
          }}
        />
      </Stack>
    );
  }

  const checkNumbers = [];
  associatedChecks.forEach((check) => {
    const checkId = check.checkId;
    let checkNumber =
      checkNumberMap[checkId] ||
      checkNumberMap[String(checkId)] ||
      checkNumberMap[Number(checkId)];

    if (checkNumber !== undefined) {
      checkNumbers.push(checkNumber);
    } else {
      checkNumbers.push(checkId);
    }
  });

  if (checkNumbers.length === 0) {
    return (
      <Stack direction="row" sx={{ marginLeft: 1, mt: 0.5 }}>
        <Chip
          label="No mapped checks"
          size="small"
          sx={{
            height: 18,
            fontSize: "0.65rem",
            fontWeight: 500,
            backgroundColor: "#f8fafc",
            color: "#64748b",
            border: "1px solid #e2e8f0",
            "& .MuiChip-label": {
              padding: "0 4px",
            },
          }}
        />
      </Stack>
    );
  }

  const visibleChecks = checkNumbers.slice(0, 4);
  const remainingCount = checkNumbers.length - 4;

  return (
    <Stack
      direction="row"
      spacing={0.3}
      sx={{ marginLeft: 1, mt: 0.5, flexWrap: "wrap", gap: 0.3 }}
    >
      {visibleChecks.map((num, index) => (
        <Chip
          key={`${num}-${index}`}
          label={`#${num}`}
          size="small"
          sx={{
            height: 18,
            fontSize: "0.65rem",
            fontWeight: 600,
            backgroundColor: "#f0fdf4",
            color: "#15803d",
            border: "1px solid #bbf7d020",
            borderRadius: "6px",
            "& .MuiChip-label": {
              padding: "0 4px",
            },
          }}
        />
      ))}
      {remainingCount > 0 && (
        <Chip
          label={`+${remainingCount}`}
          size="small"
          sx={{
            height: 18,
            fontSize: "0.65rem",
            fontWeight: 600,
            backgroundColor: "#f1f5f9",
            color: "#475569",
            border: "1px solid #e2e8f0",
            borderRadius: "6px",
            "& .MuiChip-label": {
              padding: "0 4px",
            },
          }}
        />
      )}
    </Stack>
  );
};

export const renderCheckDetails = (
  associatedChecks,
  validationStatus,
  checkNumberMap,
  getCheckStatusFromControl
) => {
  if (
    validationStatus.status !== EvidenceStatus?.VALID ||
    !validationStatus.showChecks
  ) {
    return (
      <Box sx={{ p: 2, backgroundColor: "#f5f5f5", textAlign: "center" }}>
        <Typography variant="body2" color="textSecondary">
          {validationStatus.status === EvidenceStatus?.PROCESSING
            ? "File is being processed. Check details will be available once processing is complete."
            : "File is invalid. No checks are available for this evidence file."}
        </Typography>
      </Box>
    );
  }

  if (!associatedChecks || associatedChecks.length === 0) {
    return (
      <Box sx={{ p: 2, backgroundColor: "#f5f5f5", textAlign: "center" }}>
        <Typography variant="body2" color="textSecondary">
          File is valid but no checks are associated with this evidence file.
        </Typography>
      </Box>
    );
  }

  const checksWithStatus = associatedChecks.map((check) => {
    const checkNumber =
      checkNumberMap[check.checkId] ||
      checkNumberMap[String(check.checkId)] ||
      checkNumberMap[Number(check.checkId)] ||
      check.checkId;

    return {
      ...check,
      checkStatus: getCheckStatusFromControl(check.checkId),
      checkNumber: checkNumber,
    };
  });

  // All checks are considered passed
  const passedChecks = checksWithStatus;

  return (
    <Box sx={{ p: 2, backgroundColor: "#fafbfc" }}>
      <Stack spacing={2}>
        {/* Passed Checks */}
        {passedChecks.length > 0 && (
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1.5 }}>
              <Box
                sx={{
                  width: 6,
                  height: 6,
                  borderRadius: "50%",
                  backgroundColor: "#22c55e",
                  mr: 1.5,
                }}
              />
              <Typography variant="body2" fontWeight={600} color="#15803d">
                Checks Linked ({passedChecks.length})
              </Typography>
            </Box>
            <Stack spacing={1}>
              {passedChecks.map((check, idx) => (
                <Box
                  key={check.checkId}
                  sx={{
                    p: 1.5,
                    backgroundColor: "white",
                    borderRadius: 1,
                    border: "1px solid #e2e8f0",
                    borderLeft: "3px solid #22c55e",
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      backgroundColor: "#f0fdf4",
                      color: "#15803d",
                      fontSize: "0.7rem",
                      fontWeight: 700,
                    }}
                  >
                    #{check.checkNumber}
                  </Avatar>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      fontWeight={500}
                      color="#1f2937"
                      sx={{ lineHeight: 1.3 }}
                    >
                      {check.checkDescription}
                    </Typography>
                    <Typography variant="caption" color="#6b7280">
                      ID: {check.checkId}
                    </Typography>
                  </Box>
                  <CheckCircleIcon sx={{ color: "#22c55e", fontSize: 16 }} />
                </Box>
              ))}
            </Stack>
          </Box>
        )}
      </Stack>
    </Box>
  );
};

const CHECK_CATEGORIES = [
  {
    key: "FA",
    shortLabel: "FA",
    fullLabel: "Fully Automated",
    title: "Fully Automated",
    description:
      "Data is automatically collected from integrations, validated by the system, and the check status is updated without user intervention.",
  },
  {
    key: "PA",
    shortLabel: "PA",
    fullLabel: "Partially Automated",
    title: "Partially Automated",
    description:
      "User needs to upload the required evidence. The system will automatically validate it and update the check status accordingly.",
  },
  {
    key: "NA",
    shortLabel: "NA",
    fullLabel: "Not Automated",
    title: "Not Automated",
    description:
      "This category of checks requires user intervention. Go to the check details to upload valid evidence and justification to manually override the status.",
  },
];

export { ChecksSection, ControlDetailsCard, EvidenceListItem, EvidenceSection };
